import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_PROGRAM_DAYS,
  toggleOrdered,
  workoutToPlanSeed,
  type Session,
  type Workout,
} from "@heraion/shared";

import type { WorkoutsDiaryOptions } from "./useWorkoutsDiary";

type SelectionOptions = Pick<
  WorkoutsDiaryOptions,
  "items" | "unit" | "readWorkout" | "deleteSessions" | "confirm" | "onPlanSeed" | "onError"
>;

// #368: the diary's selection, split out of useWorkoutsDiary (file size).
// A hold starts it; each tap adds or removes, and the number is the order of
// the taps. One picked session can be planned on a day, two or more become
// the days of a program, at most MAX_PROGRAM_DAYS.
export function useDiarySelection(o: SelectionOptions) {
  const { t } = useTranslation();
  const [selecting, setSelecting] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [model, setModel] = useState<{ workouts: Workout[]; sessions: Session[] } | null>(null);

  const end = useCallback(() => {
    setSelecting(false);
    setIds([]);
  }, []);
  const start = useCallback((id: string) => {
    setSelecting(true);
    setIds([id]);
  }, []);
  const toggle = useCallback((id: string) => setIds((cur) => toggleOrdered(cur, id)), []);

  const picked = useMemo(() => {
    const byId = new Map((o.items ?? []).map((s) => [s.id, s]));
    return ids.flatMap((id) => byId.get(id) ?? []);
  }, [ids, o.items]);
  const nameOf = (s: Session) => s.name ?? t("mobile.workouts.fallbackName");

  const failed = (e: unknown) => {
    console.warn("[workouts] selection_read", e instanceof Error ? e.message : e);
    o.onError(t("mobile.workouts.loadError"));
  };
  const onPlan = async () => {
    const id = ids[0];
    const unit = o.unit;
    if (!id || !unit) return;
    setBusy(true);
    try {
      const workout = await o.readWorkout(id);
      end();
      o.onPlanSeed(workoutToPlanSeed(workout, unit));
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const onCreateProgram = async () => {
    if (picked.length > MAX_PROGRAM_DAYS) {
      o.onError(t("mobile.workouts.selectHintTooMany", { max: MAX_PROGRAM_DAYS }));
      return;
    }
    setBusy(true);
    try {
      const workouts = await Promise.all(picked.map((s) => o.readWorkout(s.id)));
      const empty = workouts.findIndex((w) => w.entries.length === 0);
      if (empty >= 0) {
        const session = picked[empty];
        o.onError(
          t("mobile.workouts.pickedEmpty", { name: session ? nameOf(session) : "" }),
        );
        return;
      }
      setModel({ workouts, sessions: picked });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async () => {
    const yes = await o.confirm({
      title: t("workout.deleteManyConfirm", { count: picked.length }),
      body: picked.map(nameOf).join(", "),
      confirmLabel: t("workout.delete"),
      cancelLabel: t("program.cancel"),
    });
    if (!yes) return;
    setDeleting(true);
    try {
      const gone = new Set(await o.deleteSessions(picked.map((s) => s.id)));
      if (gone.size === picked.length) {
        end();
        return;
      }
      // Some were refused: they stay picked, in their order, and it is said
      // once, here (the host's delete does not also raise its own banner).
      setIds((cur) => cur.filter((id) => !gone.has(id)));
      o.onError(
        gone.size === 0
          ? t("mobile.workouts.deletedNone", { count: picked.length })
          : t("mobile.workouts.deletedSome", { done: gone.size, count: picked.length }),
      );
    } finally {
      setDeleting(false);
    }
  };

  return {
    selecting,
    setSelecting,
    ids,
    picked,
    nameOf,
    busy,
    deleting,
    model,
    setModel,
    start,
    end,
    toggle,
    onPlan,
    onCreateProgram,
    onDelete,
  };
}
